'use client';

import { useState } from 'react';
import { Clock, Mail, MessageSquare, Phone, Send } from 'lucide-react';
import { useSiteSettings } from '@/lib/CmsContext';
import { resolveDirectMessageLinks } from '@/lib/socialIcons';

function contactApiUrl() {
  if (typeof window !== 'undefined') {
    // Local Bedrock: /wp/wp-json/… · Prod: /wp-json/…
    const host = window.location.hostname || '';
    if (host.endsWith('grvbuild.com')) {
      return '/wp-json/4wp/v1/contact';
    }
    return '/wp/wp-json/4wp/v1/contact';
  }
  const base = process.env.WP_API_URL || 'https://grvbuild.local/wp/wp-json/4wp/v1/grv';
  return String(base).replace(/\/grv\/?$/, '/contact');
}

/**
 * @param {{ section?: import('@/types/cms').ContactsSectionPageSection }} props
 */
export default function ContactsSection({ section = {} }) {
  const { phone: sitePhone, phoneTel, phone2, phone2Tel, email, workingHours, socialLinks, social } = useSiteSettings();
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const eyebrow = section.eyebrow || 'Контакти';
  const title = section.title || 'Напишіть або зателефонуйте';
  const socialLabel = section.social_label || 'Написати напряму';
  const formTitle = section.form_title || 'Замовити дзвінок';
  const formSubtitle = section.form_subtitle || 'Залиште номер — ми передзвонимо протягом 30 хвилин';
  const buttonLabel = section.button_label || 'Перетелефонуйте мені';
  const formNote = section.form_note || 'Передзвонюємо протягом 30 хвилин у робочий час';
  const showMessengers = section.show_messengers !== false;

  const messengerLinks = showMessengers
    ? resolveDirectMessageLinks(socialLinks, social)
    : [];

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(contactApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, comment }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSent(true);
    } catch {
      setError('Не вдалося надіслати. Спробуйте ще раз або зателефонуйте нам.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="bg-background py-20 lg:py-28 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-widest">{eyebrow}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-6">{title}</h2>
            <div className="w-16 h-0.5 bg-gold-gradient mb-8" />

            <a href={`tel:${phoneTel}`} className="flex items-center gap-4 group mb-6">
              <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors shrink-0">
                <Phone className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Телефон</p>
                <p className="text-xl font-black text-foreground group-hover:text-gold transition-colors">{sitePhone}</p>
              </div>
            </a>

            {phone2 ? (
              <a href={`tel:${phone2Tel}`} className="flex items-center gap-4 group mb-6">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors shrink-0">
                  <Phone className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Телефон 2</p>
                  <p className="text-xl font-black text-foreground group-hover:text-gold transition-colors">{phone2}</p>
                </div>
              </a>
            ) : null}

            <a href={`mailto:${email}`} className="flex items-center gap-4 group mb-8">
              <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors shrink-0">
                <Mail className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Email</p>
                <p className="text-lg font-bold text-foreground group-hover:text-gold transition-colors">{email}</p>
              </div>
            </a>

            {workingHours ? (
              <div className="flex items-center gap-4 mb-10">
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Графік роботи</p>
                  <p className="text-sm font-semibold text-foreground whitespace-pre-line">{workingHours}</p>
                </div>
              </div>
            ) : null}

            {messengerLinks.length ? (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3 font-semibold">{socialLabel}</p>
                <div className="flex flex-wrap gap-2">
                  {messengerLinks.map((item) => (
                    <a
                      key={`${item.icon}-${item.url}`}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 border border-border text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:border-gold hover:text-gold"
                    >
                      <item.Icon className="w-4 h-4" />
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="bg-card border border-border rounded-2xl p-8">
            {sent ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-gold" />
                </div>
                <h3 className="text-xl font-black text-foreground mb-2">Дякуємо!</h3>
                <p className="text-muted-foreground text-sm">
                  Ми отримали ваш запит та зателефонуємо найближчим часом.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-black text-foreground mb-1">{formTitle}</h3>
                  <p className="text-sm text-muted-foreground">{formSubtitle}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2 font-semibold">
                      Ваш телефон *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+380 XX XXX XX XX"
                        className="w-full bg-background border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold text-sm transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-2 font-semibold">
                      Короткий коментар
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-muted-foreground" />
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Наприклад: утеплення фасаду 120 м², Луцьк"
                        rows={3}
                        className="w-full bg-background border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold text-sm transition-colors resize-none"
                      />
                    </div>
                  </div>

                  {error ? <p className="text-sm text-red-400">{error}</p> : null}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gold-gradient text-dark font-bold py-3.5 rounded-lg hover:opacity-90 transition-opacity text-sm uppercase tracking-wider disabled:opacity-60"
                  >
                    <Send className="w-4 h-4" />
                    {loading ? 'Надсилаємо…' : buttonLabel}
                  </button>

                  <p className="text-xs text-muted-foreground text-center">{formNote}</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
